import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'

/**
 * Determines the receipt validation scenario from the Taggun result.
 *
 * Scenarios (from requirements):
 * 1. MISSING_DATA        – date.data = null OR grossValue.data missing, successful = false
 * 2. DUPLICATE           – similarityCheck.data not empty and similarityCheck.valid = false
 * 3. TAMPERED            – tamperDetection.isTampered = true
 * 4. DIFFERENT_PRODUCT   – matchedProductLineItems empty, successful = false
 * 5. TECHNICAL_ERROR     – HTTP error (non-200), no result object
 * 6. MISSING_RETAILER    – merchantName missing/not valid while successful = true
 * 7. GENERIC_FALLBACK    – any other failure (fallback)
 */
export const getScenario = (result, isTechnicalError) => {
    if (isTechnicalError) return 'TECHNICAL_ERROR'
    if (!result) return 'TECHNICAL_ERROR'

    // Duplicate receipt
    if (
        result.fraudDetection?.similarityCheck?.data?.length > 0 &&
        result.fraudDetection?.similarityCheck?.valid === false
    ) {
        return 'DUPLICATE'
    }

    // Fraud / tamper detection
    if (result.tamperDetection?.isTampered === true) return 'TAMPERED'

    // Different product (product not matched on receipt)
    if (
        result.successful === false &&
        Array.isArray(result.matchedProductLineItems) &&
        result.matchedProductLineItems.length === 0
    ) {
        return 'DIFFERENT_PRODUCT'
    }

    // Missing purchase date or purchase value
    if (
        result.date?.data === null ||
        result.date?.data === undefined ||
        result.matchedProductLineItems?.[0]?.unitPrice === null ||
        result.matchedProductLineItems?.[0]?.unitPrice === undefined
    ) {
        return 'MISSING_DATA'
    }

    // Missing retailer: receipt is otherwise successful, but merchant cannot be trusted
    if (
        result.successful === true &&
        (result.merchantName?.data === null ||
            result.merchantName?.data === undefined ||
            result.merchantName?.valid === false)
    ) {
        return 'MISSING_RETAILER'
    }

    return 'GENERIC_FALLBACK'
}

const ReceiptValidationAlert = ({result, isTechnicalError, onShowManualFields, onRetry}) => {
    const scenario = getScenario(result, isTechnicalError)

    const renderMessage = () => {
        switch (scenario) {
            case 'MISSING_DATA':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_missing_data"
                        defaultMessage="The receipt image is illegible. Please ensure the image is clear and complete, then try again. Please note that if you proceed with manual input, you will be able to complete the registration but the validation status will remain pending for verification and you will not be eligible for any additional benefit (should you be applying for one)."
                    />
                )
            case 'DUPLICATE':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_duplicate"
                        defaultMessage="This receipt appears to have already been uploaded to our system previously. Please ensure the image is clear and complete, then try again. Please note that if you proceed, the registration status will remain pending for verification and you will not be eligible for any additional benefit (should you be applying for one)."
                    />
                )
            case 'TAMPERED':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_tampered"
                        defaultMessage="Our system has detected anomalies or alterations in the receipt. Please ensure the image is clear and complete, then try again. Please note that if you proceed, the registration status will remain pending for verification and you will not be eligible for any additional benefit (should you be applying for one)."
                    />
                )
            case 'DIFFERENT_PRODUCT':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_different_product"
                        defaultMessage="We have found that the product listed on the receipt does not match the one you selected. Please check the information you entered. If you are certain of your product selection, you may proceed. Otherwise, please <startOver>start the process over</startOver>."
                        values={{
                            startOver: (chunks) =>
                                onRetry ? (
                                    <Text
                                        as="span"
                                        color="text.tertiary"
                                        cursor="pointer"
                                        textDecoration="underline"
                                        onClick={onRetry}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') onRetry()
                                        }}
                                    >
                                        {chunks}
                                    </Text>
                                ) : (
                                    chunks
                                ),
                        }}
                    />
                )
            case 'MISSING_RETAILER':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_missing_retailer"
                        defaultMessage={`We could not detect the store of your purchase. Please choose one from the following list or select "Other".`}
                    />
                )
            case 'TECHNICAL_ERROR':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_technical_error"
                        defaultMessage="A technical error occurred while processing the receipt. Please try again later."
                    />
                )
            case 'GENERIC_FALLBACK':
            default:
                return (
                    <FormattedMessage
                        id="product_registration.receipt_generic_error"
                        defaultMessage="The receipt image is illegible. Please ensure the image is clear and complete, then try again. Please note that if you proceed with manual input, you will be able to complete the registration but the validation status will remain pending for verification and you will not be eligible for any additional benefit (should you be applying for one)."
                    />
                )
        }
    }

    const renderActions = () => {
        switch (scenario) {
            case 'MISSING_DATA':
            case 'GENERIC_FALLBACK':
                return (
                    <>
                        {onRetry && (
                            <Text
                                as="span"
                                textStyle="body-small"
                                color="text.tertiary"
                                cursor="pointer"
                                textDecoration="underline"
                                onClick={onRetry}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') onRetry()
                                }}
                                mr={4}
                            >
                                <FormattedMessage
                                    id="product_registration.product_not_found.try_again_button"
                                    defaultMessage="Try Again"
                                />
                            </Text>
                        )}
                        <Text
                            as="span"
                            textStyle="body-small"
                            color="text.tertiary"
                            cursor="pointer"
                            textDecoration="underline"
                            onClick={onShowManualFields}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') onShowManualFields()
                            }}
                        >
                            <FormattedMessage
                                id="product_registration.receipt_continue_manual"
                                defaultMessage="Continue with manual input"
                            />
                        </Text>
                    </>
                )
            case 'DUPLICATE':
            case 'TAMPERED':
                return (
                    <>
                        {onRetry && (
                            <Text
                                as="span"
                                textStyle="body-small"
                                color="text.tertiary"
                                cursor="pointer"
                                textDecoration="underline"
                                onClick={onRetry}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') onRetry()
                                }}
                                mr={4}
                            >
                                <FormattedMessage
                                    id="product_registration.product_not_found.try_again_button"
                                    defaultMessage="Try Again"
                                />
                            </Text>
                        )}
                        <Text
                            as="span"
                            display="block"
                            textStyle="body-small"
                            color="text.tertiary"
                            cursor="pointer"
                            textDecoration="underline"
                            onClick={onShowManualFields}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') onShowManualFields()
                            }}
                        >
                            <FormattedMessage
                                id="product_registration.receipt_continue"
                                defaultMessage="Continue"
                            />
                        </Text>
                    </>
                )
            case 'TECHNICAL_ERROR':
                return (
                    <>
                        {onRetry && (
                            <Text
                                as="span"
                                textStyle="body-small"
                                color="text.tertiary"
                                cursor="pointer"
                                textDecoration="underline"
                                onClick={onRetry}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') onRetry()
                                }}
                                mr={4}
                            >
                                <FormattedMessage
                                    id="product_registration.receipt_technical_error_retry"
                                    defaultMessage="Please try again later."
                                />
                            </Text>
                        )}
                        <Text
                            as="span"
                            display="block"
                            textStyle="body-small"
                            color="text.tertiary"
                            cursor="pointer"
                            textDecoration="underline"
                            onClick={onShowManualFields}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') onShowManualFields()
                            }}
                        >
                            <FormattedMessage
                                id="product_registration.receipt_continue_manual"
                                defaultMessage="Continue with manual input"
                            />
                        </Text>
                    </>
                )
            case 'DIFFERENT_PRODUCT':
                return (
                    <Text
                        as="span"
                        textStyle="body-small"
                        color="text.tertiary"
                        cursor="pointer"
                        textDecoration="underline"
                        onClick={onShowManualFields}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') onShowManualFields()
                        }}
                    >
                        <FormattedMessage
                            id="product_registration.receipt_data_correct_continue"
                            defaultMessage="The product is correct - Continue"
                        />
                    </Text>
                )
            case 'MISSING_RETAILER':
                return null
            default:
                return null
        }
    }

    return (
        <Box bg="background.danger" borderRadius="md" p={4} mb={4} w={'100%'}>
            <Text textStyle="body-small" color="text.default" mb={2}>
                {renderMessage()}
            </Text>
            {renderActions()}
        </Box>
    )
}

ReceiptValidationAlert.propTypes = {
    result: PropTypes.object,
    isTechnicalError: PropTypes.bool,
    onShowManualFields: PropTypes.func.isRequired,
    onRetry: PropTypes.func,
}

export default ReceiptValidationAlert
